import { useState } from 'react';
import { useMachine } from '@xstate/react';

import { useAppSelector } from '../../stores/redux-store';
import { useProduct } from '../../components/ApplicationProviders/ProductProvider/ProductProvider';
import { useVersion } from '../../components/ApplicationProviders/VersionProvider/VersionProvider';
import { routeBuilder } from '../../routes/routeBuilder';
import { CreateUrlHandoffSessionRequest } from '../../lib-api/services/HandoffSession';
import {
  createHandoffCreatorMachine,
  HandoffCreatorMachineProps,
} from '../machines/handoffCreatorMachine/handoffCreatorMachine';
import { applicationParameters } from '../../utilities/application-parameters';
import { ProviderEntity } from '../../models/provider';
import { CredentialsEntity } from '../../models/credentials';

export type UseHandoffCreatorMachineProps = Pick<
  HandoffCreatorMachineProps,
  'onDone' | 'onHandoffUpdated' | 'handoffCredentialsAction'
> & {
  provider: ProviderEntity;
  providedCredentials?: CredentialsEntity;
};

export const useHandoffCreatorMachine = ({
  providedCredentials,
  provider,
  handoffCredentialsAction,
  onDone,
  onHandoffUpdated,
}: UseHandoffCreatorMachineProps) => {
  const { accessToken, idHint } = useAppSelector((state) => state.user);
  const { productType, productDimension } = useProduct();
  const {
    version: { version },
  } = useVersion();
  const trackingId = applicationParameters.get('trackingId');

  const urlToMinimize = routeBuilder.buildHandoffUrlToMinimize.build({
    version,
    productType,
    productDimension,
    authorizationToken: accessToken!,
    handoffCredentialsAction,
    trackingId: trackingId!,
    market: provider.market,
    path: '/mobile-handoff',
  });
  const createUrlHandoffSessionRequest: CreateUrlHandoffSessionRequest = {
    providerName: provider.name,
    userAccessToken: accessToken!,
    sessionId: trackingId!,
    url: urlToMinimize,
    credentialsId: providedCredentials?.id,
  };
  const { origin } = window.location;

  const [machine] = useState(
    createHandoffCreatorMachine({
      initialContext: {
        provider,
        userHintId: idHint,
        accessToken,
      },
      createUrlHandoffSessionRequest,
      version,
      origin,
      onDone,
      onHandoffUpdated,
      handoffCredentialsAction,
    })
  );

  return useMachine(machine);
};
